import { useMemo, useState } from "react";
import { Apple, Download, ExternalLink, Play, QrCode, Smartphone, X, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Bouton universel de téléchargement de l'app mobile PROGÈRE (une seule app pour tous les modules).
const APP_STORE_URL = process.env.REACT_APP_PROGERE_APP_STORE_URL || "";
const GOOGLE_PLAY_URL = process.env.REACT_APP_PROGERE_GOOGLE_PLAY_URL || "";
const MOBILE_LANDING_URL = process.env.REACT_APP_PROGERE_MOBILE_LANDING_URL || "https://progere.ca/application";
const APP_DEEP_LINK = process.env.REACT_APP_PROGERE_APP_DEEP_LINK || "progere://home";
const STORES_LIVE = Boolean(APP_STORE_URL && GOOGLE_PLAY_URL);

function getPlatform() {
  if (typeof navigator === "undefined") return "desktop";
  const ua = navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)) return "ios";
  if (/android/.test(ua)) return "android";
  return "desktop";
}

export function ProgereDownloadAppButton({ variant = "card", size = "md", moduleCode = "punch", showSubtitle = true, showQrOnDesktop = true, className = "" }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const platform = useMemo(() => getPlatform(), []);

  const trackingUrl = useMemo(() => {
    try {
      const url = new URL(MOBILE_LANDING_URL);
      url.searchParams.set("source", "web");
      url.searchParams.set("module", moduleCode);
      url.searchParams.set("campaign", "download_app");
      return url.toString();
    } catch { return MOBILE_LANDING_URL; }
  }, [moduleCode]);

  const openStore = () => {
    if (platform === "ios") { window.location.href = APP_STORE_URL || trackingUrl; return; }
    if (platform === "android") { window.location.href = GOOGLE_PLAY_URL || trackingUrl; return; }
    setOpen(true);
  };

  const tryOpenApp = () => {
    const timer = window.setTimeout(() => { window.open(trackingUrl, "_blank", "noopener"); }, 1300);
    window.addEventListener("blur", () => window.clearTimeout(timer), { once: true });
    window.location.href = APP_DEEP_LINK;
  };

  const copyLink = () => {
    navigator.clipboard?.writeText(trackingUrl).then(() => {
      setCopied(true); toast.success("Lien copié"); setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Impossible de copier"));
  };

  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=0&data=${encodeURIComponent(trackingUrl)}`;
  const cls = ["pg-dl-btn", `pg-dl-btn--${variant}`, `pg-dl-btn--${size}`, className].filter(Boolean).join(" ");

  return (
    <>
      <button type="button" className={cls} onClick={openStore} data-testid={`download-app-btn-${variant}`} aria-label="Télécharger l'application PROGÈRE">
        <span className="pg-dl-btn__icon"><Smartphone size={19} aria-hidden="true" /></span>
        <span className="pg-dl-btn__content">
          <span className="pg-dl-btn__title">Télécharger l'application</span>
          {showSubtitle && <span className="pg-dl-btn__subtitle">{STORES_LIVE ? "Disponible sur Apple et Android" : "Apple et Android · bientôt"}</span>}
        </span>
        <Download className="pg-dl-btn__action" size={17} aria-hidden="true" />
      </button>

      {open && (
        <div className="support-overlay" role="presentation" onMouseDown={() => setOpen(false)} data-testid="download-app-modal">
          <section className="card gold-border" role="dialog" aria-modal="true" style={{ width: 480, maxWidth: "100%", position: "relative" }} onMouseDown={(e) => e.stopPropagation()}>
            <button type="button" className="top-btn" onClick={() => setOpen(false)} aria-label="Fermer" style={{ position: "absolute", top: 14, right: 14 }}><X size={18} /></button>

            <div style={{ textAlign: "center", marginBottom: 6 }}>
              <div className="progere-brand-metal progere-brand-metal--medium" style={{ justifyContent: "center" }}>
                <span className="progere-brand-prog">PRO</span><span className="progere-brand-ere">GÈRE</span><span className="progere-brand-module">MOBILE</span>
              </div>
            </div>
            <h2 style={{ textAlign: "center", fontSize: 18, fontWeight: 800, margin: "6px 0 4px" }}>Téléchargez l'application PROGÈRE</h2>
            <p style={{ textAlign: "center", color: "var(--muted)", fontSize: 13, margin: "0 0 18px", lineHeight: 1.5 }}>
              Une seule application pour tous vos modules PROGÈRE. Vos modules actifs apparaissent automatiquement après connexion.
            </p>

            {!STORES_LIVE && (
              <div className="pg-dl-soon" data-testid="download-soon-note">
                Les fiches App Store et Google Play arrivent bientôt. En attendant, consultez la page officielle de l'application.
              </div>
            )}

            <div className="pg-dl-stores">
              <a href={APP_STORE_URL || trackingUrl} className="pg-store-btn" target="_blank" rel="noopener noreferrer" data-testid="download-appstore">
                <Apple size={24} /><span><small>Télécharger dans</small><strong>l'App Store</strong></span>
              </a>
              <a href={GOOGLE_PLAY_URL || trackingUrl} className="pg-store-btn" target="_blank" rel="noopener noreferrer" data-testid="download-googleplay">
                <Play size={22} /><span><small>Disponible sur</small><strong>Google Play</strong></span>
              </a>
            </div>

            {showQrOnDesktop && (
              <div className="pg-dl-qr">
                <img src={qrSrc} alt="Code QR de téléchargement PROGÈRE" width={90} height={90}
                  onError={(e) => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                <div className="pg-dl-qr__fallback" style={{ display: "none" }}><QrCode size={64} /></div>
                <div><strong>Scannez avec votre téléphone</strong><p style={{ margin: "4px 0 0", fontSize: 11.5, color: "var(--muted)", wordBreak: "break-all" }}>{trackingUrl}</p></div>
              </div>
            )}

            <div className="pg-dl-actions">
              <button type="button" className="btn btn-secondary" onClick={tryOpenApp} data-testid="download-open-app">Ouvrir PROGÈRE <ExternalLink size={15} /></button>
              <button type="button" className="btn btn-gold" onClick={copyLink} data-testid="download-copy-link">{copied ? <Check size={15} /> : <Copy size={15} />} Copier le lien</button>
            </div>
          </section>
        </div>
      )}
    </>
  );
}
